import { useEffect, useMemo, useState } from 'react';
import { OperationResult } from 'hooks/store/types';
import { useTranslation } from 'hooks/store/ui/useTranslation';
import {
	ActionTypes,
	UserLicenseModel,
	getLicenseModels,
	UserLicenseLabels,
	SubscriptionLicenseModel,
	LicenseModelQuantities
} from 'store/account/subscription';
import { useDispatch, useSelector } from 'hooks/utils';
import { useActivity } from 'hooks/store/utils/useActivity';
import { GenericMap } from 'types';
import { useSubscription } from './useSubscription';
import { useSubscriptionBundleFlag } from './useSubscriptionBundleFlag';
import { useAccount } from './useAccount';
import { mapLicenseModelIdQuantitiesToLicenseModelQuantities } from 'helpers/subscription';
import { useAuth } from 'features/auth/AuthProvider';
import { useGetIsUserAdminQuery } from 'features/data/useGetIsUserAdminQuery';

type LicenseModelsType = {
	allLicenseOptions: LicenseOption[]; // contains all ledidi license models name and label
	availableLicenseOptions: LicenseOption[]; // contains license models name and label available for the subscription
	licenseModelsMetadataById: GenericMap<SubscriptionLicenseModel>; // all license ids with prices per unit

	// old version
	totalLicensesCount: number;
	usedLicensesCount: number;
	availableLicensesCount: number;
	// bundle version
	licenseQuantitiesByName?: LicenseModelQuantities; // by license model name
	usedLicenseQuantitiesByName?: LicenseModelQuantities; // by license model name
	availableLicenseQuantitiesByName?: LicenseModelQuantities; // by license model name
	// from subscriptionDetails
	licenseQuantitiesById?: LicenseModelQuantities;
	availableLicenseQuantitiesById?: LicenseModelQuantities;
};

type LicenseOption = { label: string; value: string };

export function useLicenseModels(): OperationResult<LicenseModelsType> {
	const { translate } = useTranslation();

	const dispatch = useDispatch();

	const { data: isUserAdmin } = useGetIsUserAdminQuery();

	const [allLicenseOptions, setAllLicenseOptions] = useState<LicenseOption[]>([]);
	const [availableLicenseOptions, setAvailableLicenseOptions] = useState<LicenseOption[]>([]);

	const { subscriptionBundleFlag } = useSubscriptionBundleFlag();
	const [{ data: userAccountData }] = useAccount();

	const { licenseModelsMetadataById, allLicenseModels, fetched } = useSelector(
		state => state.account.subscription.licenseModels
	);

	const [
		{
			data: {
				details: subscriptionDetails,
				subscriptionTypes: { isLedidiFree, isLedidiFreeTrial, isNonSubscriber },
				userTypesAndRoles: {
					isPaidSubscriptionOwner,
					ledidiEnterprise: {
						modules: { hasEnterpriseAdminRights }
					}
				}
			}
		}
	] = useSubscription();

	const [{ loading, error }] = useActivity(ActionTypes.GET_LICENSE_MODELS);

	useEffect(() => {
		if (!fetched && !loading && !error) handler();
	}, [fetched, loading, error]);

	function handler() {
		// TODO: this will be taken from getUserSubscription in the future
		// for now only user admins need this call
		if (isUserAdmin) dispatch(getLicenseModels());
	}

	useEffect(() => {
		if (fetched) {
			const options: LicenseOption[] = allLicenseModels.map(licenseModel => ({
				label:
					translate(() => UserLicenseLabels[licenseModel as UserLicenseModel]) ??
					licenseModel,
				value: licenseModel
			}));

			setAllLicenseOptions(options);
		} else if (!isUserAdmin && !subscriptionBundleFlag) {
			// for now the existing license models were set on FE
			setAllLicenseOptions([
				{
					label:
						translate(() => UserLicenseLabels[UserLicenseModel.OneOwnedOneShared]) ??
						UserLicenseModel.OneOwnedOneShared,
					value: UserLicenseModel.OneOwnedOneShared
				},
				{
					label:
						translate(() => UserLicenseLabels[UserLicenseModel.Trial]) ??
						UserLicenseModel.Trial,
					value: UserLicenseModel.Trial
				},
				{
					label:
						translate(() => UserLicenseLabels[UserLicenseModel.Collaborator]) ??
						UserLicenseModel.Collaborator,
					value: UserLicenseModel.Collaborator
				},
				{
					label:
						translate(() => UserLicenseLabels[UserLicenseModel.Full]) ??
						UserLicenseModel.Full,
					value: UserLicenseModel.Full
				}
			]);
		}
	}, [allLicenseModels, fetched]);

	useEffect(() => {
		const options: LicenseOption[] = [];
		const availableLicenseQuantities =
			subscriptionDetails && subscriptionDetails.availableLicenseQuantities;

		if (subscriptionBundleFlag && availableLicenseQuantities) {
			Object.keys(availableLicenseQuantities).forEach(licenseModelKey => {
				if (
					licenseModelsMetadataById[licenseModelKey] &&
					availableLicenseQuantities[licenseModelKey] > 0
				) {
					const licenseModel = licenseModelsMetadataById[licenseModelKey].licenseModel;
					options.push({
						label:
							translate(() => UserLicenseLabels[licenseModel as UserLicenseModel]) ??
							licenseModel,
						value: licenseModel
					});
				}
			});

			setAvailableLicenseOptions(options);
		} else {
			// for old version when we don't use bundle subscription, return default licenses
			setAvailableLicenseOptions([
				{
					label: translate(() => UserLicenseLabels[UserLicenseModel.Full]),
					value: UserLicenseModel.Full
				},
				{
					label: translate(() => UserLicenseLabels[UserLicenseModel.Collaborator]),
					value: UserLicenseModel.Collaborator
				}
			]);
		}
	}, [subscriptionDetails, subscriptionBundleFlag, licenseModelsMetadataById]);

	// Licenses
	const isNoLicenseAccount = useMemo(
		() => isLedidiFree || isLedidiFreeTrial || isNonSubscriber,
		[isLedidiFree, isLedidiFreeTrial, isNonSubscriber]
	);
	const isPaidUserAccount = useMemo(
		() => !isPaidSubscriptionOwner && !hasEnterpriseAdminRights,
		[isPaidSubscriptionOwner, hasEnterpriseAdminRights]
	);

	let licenseQuantitiesByName: LicenseModelQuantities = {};
	let usedLicenseQuantitiesByName: LicenseModelQuantities = {};
	let availableLicenseQuantitiesByName: LicenseModelQuantities = {};
	if (subscriptionBundleFlag) {
		if (isPaidUserAccount && userAccountData?.details && userAccountData.details.licenseModel) {
			// if the logged in user is part of a subscription and doesn't have any admin rights, set only his license
			licenseQuantitiesByName = { [userAccountData.details.licenseModel]: 1 };
			usedLicenseQuantitiesByName = { [userAccountData.details.licenseModel]: 1 };
		} else if (
			subscriptionDetails &&
			subscriptionDetails.licenseQuantities &&
			subscriptionDetails.availableLicenseQuantities
		) {
			licenseQuantitiesByName = mapLicenseModelIdQuantitiesToLicenseModelQuantities(
				subscriptionDetails.licenseQuantities,
				licenseModelsMetadataById
			);
			availableLicenseQuantitiesByName = mapLicenseModelIdQuantitiesToLicenseModelQuantities(
				subscriptionDetails.availableLicenseQuantities,
				licenseModelsMetadataById
			);
			Object.keys(licenseQuantitiesByName).forEach(key => {
				usedLicenseQuantitiesByName = {
					...usedLicenseQuantitiesByName,
					[key]: availableLicenseQuantitiesByName[key]
						? licenseQuantitiesByName[key] - availableLicenseQuantitiesByName[key]
						: licenseQuantitiesByName[key]
				};
			});
		}
	}
	const totalLicensesCount =
		isNoLicenseAccount || !subscriptionDetails
			? 0
			: isPaidUserAccount
			? 1
			: calculateLicensesQuantity(
					subscriptionDetails && subscriptionDetails.licenseQuantity
						? subscriptionDetails.licenseQuantity
						: 0,
					subscriptionDetails.licenseQuantities
			  );

	const availableLicensesCount =
		isNoLicenseAccount || isPaidUserAccount || !subscriptionDetails
			? 1
			: calculateLicensesQuantity(
					subscriptionDetails && subscriptionDetails.availableLicenseQuantity
						? subscriptionDetails.availableLicenseQuantity
						: 0,
					subscriptionDetails.availableLicenseQuantities
			  );

	const usedLicensesCount = isNoLicenseAccount
		? 0
		: isPaidUserAccount
		? 1
		: calculateLicensesQuantity(
				subscriptionDetails &&
					subscriptionDetails.availableLicenseQuantity !== undefined &&
					subscriptionDetails.licenseQuantity !== undefined
					? subscriptionDetails.licenseQuantity -
							subscriptionDetails.availableLicenseQuantity
					: 0,
				usedLicenseQuantitiesByName
		  );

	function calculateLicensesQuantity(licenses: number, bundleLicenses?: LicenseModelQuantities) {
		if (subscriptionBundleFlag && bundleLicenses) {
			let sum = 0;
			Object.values(bundleLicenses).forEach(v => (sum = sum + v));
			return sum;
		}
		return licenses;
	}

	return [
		{
			data: {
				allLicenseOptions: allLicenseOptions,
				availableLicenseOptions: availableLicenseOptions,
				licenseModelsMetadataById: licenseModelsMetadataById,
				totalLicensesCount: totalLicensesCount,
				availableLicensesCount: availableLicensesCount,
				usedLicensesCount: usedLicensesCount,
				licenseQuantitiesByName: licenseQuantitiesByName,
				availableLicenseQuantitiesByName: availableLicenseQuantitiesByName,
				usedLicenseQuantitiesByName: usedLicenseQuantitiesByName,
				licenseQuantitiesById: subscriptionDetails?.licenseQuantities,
				availableLicenseQuantitiesById: subscriptionDetails?.availableLicenseQuantities
			},
			loading,
			error,
			fetched: fetched
		},
		handler
	];
}
